import { style } from 'typestyle';

////////////////////////////////////////////////////////////////////////////////
//
//  A lot of work was done to hide the "Compare" column on diamond search.
//
//  This was done specifically to facilitate the loose diamond search funnel.
//
//  If that is ever removed, review commits
//  a96b822e88dcc382dbc20021fab688da2707e41c through
//  6f59e53f629729033cbb9ff9090072f7a676b43d
//  to see what changes can be undone
//  to simplify the code.
//
////////////////////////////////////////////////////////////////////////////////

const staticColumns = (allColumns, hideableColumns) => {
  // Return all columns which are not hideable
  return allColumns.filter(column => hideableColumns.indexOf(column) === -1);
};

const hideableColumns = selectedFilters => {
  const hideableUnlessSelectedFilter = [
    'lw_ratio',
    'table',
    'depth',
    'fluorescence',
    'symmetry',
    'polish'
  ];

  // Return all hideable columns which are not also selected filters
  return hideableUnlessSelectedFilter.filter(
    column => selectedFilters.indexOf(column) === -1
  );
};

const calculateVisibilities = (selectedFilters, allowCompare = true) => {
  type resultType = {
    all: Array<string>,
    hideable: null | Array<string>,
    static: null | Array<string>
  };

  let allColumns = [
    'compare',
    'shape',
    'carat',
    'cut',
    'color',
    'clarity',
    'polish',
    'symmetry',
    'fluorescence',
    'depth',
    'table',
    'lw_ratio',
    'price'
  ];

  if (!allowCompare) allColumns = allColumns.slice(1);

  const result: resultType = {
    all: allColumns,
    hideable: null,
    static: null
  };

  result.hideable = hideableColumns(selectedFilters);
  result.static = staticColumns(result.all, result.hideable);

  return result;
};

const calculateBreakpoints = (columnWidths, columnVisibilities) => {
  if (!columnVisibilities.hideable) return [];

  type resultType = Array<{
    column: string,
    breakpoint: number,
    columnWidth: number,
    smallestBreakpoint: boolean
  }>;

  const result: resultType = columnVisibilities.hideable.reduce(
    (all, column, index, source): Array<Object> => {
      const previousBreakpoint = all[all.length - 1];
      const columnWidth = columnWidths[column];
      const breakpoint = previousBreakpoint
        ? previousBreakpoint['breakpoint'] - previousBreakpoint['columnWidth']
        : 1000;

      return [
        ...all,
        {
          column,
          breakpoint,
          columnWidth,
          smallestBreakpoint: index === source.length - 1
        }
      ];
    },
    []
  );

  return result;
};

const calculateBreakpointClasses = (
  breakpoints,
  allowCompare
): { [string]: string, allowCompare: boolean } => {
  const classes = {};
  const priceStyles = {};

  breakpoints.forEach((breakpoint, index) => {
    const columnName = breakpoint.column;
    const maxWindowWidth = breakpoint.breakpoint - 1 + 20; // add 20px for windows scrollbars
    const priceWidth = allowCompare ? 90 : 165;
    const nextBreakpoint = breakpoints[index + 1];
    let priceWidthSubtractor;

    if (nextBreakpoint) {
      priceWidthSubtractor = nextBreakpoint.breakpoint - priceWidth;
    } else {
      priceWidthSubtractor =
        breakpoint.breakpoint - breakpoint.columnWidth - priceWidth;
    }

    classes[columnName] = style({
      [`@media (max-width: ${maxWindowWidth}px)`]: {
        display: 'none'
      }
    });

    priceStyles['min-width'] = priceWidth;

    if (breakpoint.smallestBreakpoint) {
      priceStyles[
        `@media (min-width: 1px) and (max-width: ${maxWindowWidth}px)`
      ] = {
        width: `calc(100% - ${priceWidthSubtractor}px)`,
        minWidth: `calc(100% - ${priceWidthSubtractor}px)`
      };
    } else {
      const minWindowWidth = breakpoint.breakpoint - breakpoint.columnWidth;

      priceStyles[
        `@media (min-width: ${minWindowWidth}px) and (max-width: ${maxWindowWidth}px)`
      ] = {
        width: `calc(100% - ${priceWidthSubtractor}px)`,
        minWidth: `calc(100% - ${priceWidthSubtractor}px)`
      };
    }
  });

  classes.price = style(priceStyles);

  return classes;
};

const getTableMinimumWidthFromBreakpoints = breakpoints => {
  if (breakpoints.length === 0) return 1000; // all filters are selected, we can't hide any columns

  const smallestBreakpoint = breakpoints.find(
    breakpoint => breakpoint.smallestBreakpoint
  );

  // Fallback just in case for any reason smallestBreakpoint can't be found
  if (!smallestBreakpoint) return 519;

  return smallestBreakpoint.breakpoint - smallestBreakpoint.columnWidth;
};

const mergeWithLargestBreakpoints = (set1, set2) => {
  let mergedSet1 = [];
  let mergedSet2 = [];

  for (let i = 0; i < set1.length; i++) {
    const largerBreakpoint = Math.max(set1[i].breakpoint, set2[i].breakpoint);

    mergedSet1 = [
      ...mergedSet1,
      {
        ...set1[i],
        breakpoint: largerBreakpoint
      }
    ];

    mergedSet2 = [
      ...mergedSet2,
      {
        ...set2[i],
        breakpoint: largerBreakpoint
      }
    ];
  }

  return [mergedSet1, mergedSet2];
};

export function calculateColumnBreakpoints(
  forceShownColumns,
  headerColumnWidths,
  rowColumnWidths,
  allowCompare
) {
  const columnVisibilities = calculateVisibilities(
    forceShownColumns,
    allowCompare
  );
  let headerColumnBreakpoints = calculateBreakpoints(
    headerColumnWidths,
    columnVisibilities,
    allowCompare
  );
  let rowColumnBreakpoints = calculateBreakpoints(
    rowColumnWidths,
    columnVisibilities,
    allowCompare
  );

  return mergeWithLargestBreakpoints(
    headerColumnBreakpoints,
    rowColumnBreakpoints
  );
}

export function calculateBreakpointClassesAndMinTableWidth(
  breakpoints,
  allowCompare
) {
  const columnBreakpointClasses = calculateBreakpointClasses(
    breakpoints,
    allowCompare
  );

  const tableMinimumWidth =
    getTableMinimumWidthFromBreakpoints(breakpoints) - (allowCompare ? 0 : 75);

  return {
    classes: columnBreakpointClasses,
    tableMinimumWidth
  };
}
